<template>
    <HomeLayout>
        <!-- PAGE HERO -->
        <div class="page-hero">
            <div class="page-hero-inner">
                <div class="page-hero-top">
                    <div>
                        <h1>Find a <em>verified</em> Care Giver<br>near you</h1>
                    </div>
                </div>
            </div>
            <div class="search-bar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A9E98" stroke-width="2"
                    stroke-linecap="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input type="text" v-model="searchQuery" placeholder="Search by name, skill or location..." />
                <select v-model="selectedState">
                    <option value="">All States</option>
                    <option v-for="state in states" :key="state.alias" :value="state.state">
                        {{ state.state }}
                    </option>
                </select>
                <button class="btn-search">Search</button>
            </div>
        </div>

        <div class="page-body">

            <!-- FILTER SIDEBAR -->
            <aside class="filter-sidebar" id="filterSidebar">
                <div class="filter-header">
                    <h3>Filters</h3>
                    <button class="filter-clear" @click="clearFilters">Clear all</button>
                </div>

                <div class="filter-section">
                    <div class="filter-label">Care Type</div>
                    <div class="filter-options">
                        <div class="filter-option">
                            <input type="checkbox" value="child care" v-model="filters.type" />
                            <label>👶 Child Care</label>
                        </div>
                        <div class="filter-option">
                            <input type="checkbox" value="elder care" v-model="filters.type" />
                            <label>👶 Elderly Care</label>
                        </div>
                        <div class="filter-option">
                            <input type="checkbox" value="house keeper" v-model="filters.type" />
                            <label>🏠 Housekeeper</label>
                        </div>
                    </div>
                </div>

                <div class="filter-section">
                    <div class="filter-label">Work Arrangement</div>
                    <div class="filter-chips" id="arrangementFilter">
                        <div class="f-chip" :class="{ active: filters.arrangement.includes('live_in') }"
                            @click="toggleArrangement('live_in')">Live-in</div>
                        <div class="f-chip" :class="{ active: filters.arrangement.includes('live_out') }"
                            @click="toggleArrangement('live_out')">Live-out</div>
                        <div class="f-chip" :class="{ active: filters.arrangement.includes('both') }"
                            @click="toggleArrangement('both')">Both</div>
                    </div>
                </div>

                <div class="filter-section">
                    <div class="filter-label">Availability</div>
                    <div class="filter-chips" id="availFilter">
                        <div class="f-chip" :class="{ active: filters.availability.includes('now') }"
                            @click="toggleAvailability('now')">Available now</div>
                        <div class="f-chip" :class="{ active: filters.availability.includes('week') }"
                            @click="toggleAvailability('week')">This week</div>
                        <div class="f-chip" :class="{ active: filters.availability.includes('month') }"
                            @click="toggleAvailability('month')">This month</div>
                    </div>
                </div>

                <div class="filter-section">
                    <div class="filter-label">Experience</div>
                    <div class="filter-options">
                        <div class="filter-option">
                            <input type="checkbox" value="1" v-model="filters.experience" />
                            <label>Less than 1 year</label>
                        </div>

                        <div class="filter-option">
                            <input type="checkbox" value="1-3" v-model="filters.experience" />
                            <label>1-3 years</label>
                        </div>

                        <div class="filter-option">
                            <input type="checkbox" value="3-5" v-model="filters.experience" />
                            <label>3-5 years</label>
                        </div>


                        <div class="filter-option">
                            <input type="checkbox" value="5+" v-model="filters.experience" />
                            <label>5+ years</label>
                        </div>

                    </div>
                </div>

                <div class="filter-section">
                    <div class="filter-label">Status</div>
                    <div class="filter-option">

                        <input type="checkbox" v-model="filters.verified">
                        <label>✓ Verified</label>
                    </div>
                </div>



                <button class="btn-apply-filters" @click="applyFilters">
                    Apply Filters
                </button>
            </aside>

            <!-- RESULTS -->
            <div>
                <div class="results-header">
                    <div class="results-count">Showing <strong>{{ filteredCaregivers.length }}</strong> verified Care
                        Givers</div>
                    <div class="sort-row">
                        <span class="sort-label">Sort by:</span>
                        <select class="sort-select" v-model="sortBy">
                            <option value="match">Best Match</option>
                            <option value="rating">Highest Rated</option>
                            <option value="experience">Most Experienced</option>
                            <option value="newest">Recently Added</option>
                        </select>
                        <div class="view-toggle">
                            <button class="view-btn" :class="{ active: viewMode === 'grid' }" @click="setView('grid')"
                                title="Grid view">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="3" width="7" height="7" rx="1" />
                                    <rect x="14" y="3" width="7" height="7" rx="1" />
                                    <rect x="3" y="14" width="7" height="7" rx="1" />
                                    <rect x="14" y="14" width="7" height="7" rx="1" />
                                </svg>
                            </button>

                            <button class="view-btn" :class="{ active: viewMode === 'list' }" @click="setView('list')"
                                title="List view">
                                <svg viewBox="0 0 24 24" fill="none">
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <line x1="3" y1="12" x2="21" y2="12" />
                                    <line x1="3" y1="18" x2="21" y2="18" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="active-filters" v-if="activeFilters.length">

                    <div class="af-tag" v-for="f in activeFilters" :key="f.key + f.value">
                        {{ f.label }}
                        <button @click="removeFilter(f)">✕</button>
                    </div>

                </div>

                <div v-if="isLoading" class="cg-grid">
                    <div v-for="n in 6" :key="n" class="cg-card skeleton">
                        <div class="cg-photo skeleton-box"></div>
                        <div class="cg-info">
                            <div class="skeleton-line"></div>
                            <div class="skeleton-line short"></div>
                        </div>
                    </div>
                </div>

                <div v-else class="cg-grid" :class="{ 'list-view': viewMode === 'list' }">
                    <div class="cg-card" v-for="cg in paginatedCaregivers" :key="cg.id">
                        <div class="cg-photo">
                            <img :src="cg.image" :alt="cg.name" />
                            <div class="cg-verified" v-if="cg.verified">✓ Verified</div>
                        </div>

                        <div class="cg-info">
                            <div class="cg-name">{{ cg.name }}</div>

                            <div class="cg-role">
                                {{ formatCareType(cg.care_type) }}
                            </div>

                            <div class="cg-avail">
                                <span class="avail-dot"></span>
                                {{ cg.available ? 'Available now' : 'Unavailable' }}
                            </div>

                            <div class="cg-btn-row">
                                <button class="cg-btn-request" @click="requestCG(cg.name)">
                                    Request
                                </button>

                                <button class="cg-btn-details" @click="openCGModal(cg)">
                                    Carer Details
                                </button>
                            </div>
                        </div>
                    </div>
                </div><!-- /cg-grid -->

                <!-- PAGINATION -->
                <div class="pagination">

                    <button class="page-btn nav" @click="prev" :disabled="!prevPage">
                        ← Prev
                    </button>

                    <button v-for="p in pages" :key="p" class="page-btn" :class="{ active: p === currentPage }"
                        @click="goToPage(p)">
                        {{ p }}
                    </button>

                    <button class="page-btn nav" @click="next" :disabled="!nextPage">
                        Next →
                    </button>

                </div>
            </div>

        </div><!-- /page-body -->

        <div v-if="isModalOpen" class="cg-modal-overlay open" @click.self="closeCGModal">
            <div class="cg-modal">

                <!-- Header -->
                <div class="cg-modal-header">
                    <div class="cg-modal-avatar">
                        <img :src="selectedCG?.image" v-if="selectedCG?.image" />
                    </div>

                    <div>
                        <div class="cg-modal-name">
                            {{ selectedCG.name }}
                        </div>

                        <div class="cg-modal-role">
                            {{ formatCareType(selectedCG.care_type) }}
                        </div>
                    </div>

                    <button class="cg-modal-close" @click="closeCGModal">✕</button>
                </div>

                <!-- Body -->
                <div class="cg-modal-body">

                    <a :href="`https://wa.me/2348090804000?text=${encodeURIComponent('Hello, I want to request ' + selectedCG.name)}`"
                        target="_blank" class="cg-modal-wa">
                        Request on WhatsApp
                    </a>

                    <!-- Education -->
                    <div class="cg-modal-section">
                        <div class="cg-modal-section-title">
                            <svg viewBox="0 0 24 24">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                            </svg>
                            Education
                        </div>

                        <ul>
                            <li v-for="edu in selectedCG.educations" :key="edu.id">
                                {{ edu.title }}
                            </li>
                        </ul>
                    </div>

                    <!-- Speciality -->
                    <div class="cg-modal-section">
                        <div class="cg-modal-section-title">
                            <svg viewBox="0 0 24 24">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                            </svg>
                            Speciality
                        </div>

                        <ul>
                            <li v-for="spec in selectedCG.specialities" :key="spec.id">
                                {{ spec.name }}
                            </li>
                        </ul>
                    </div>

                    <!-- Experience -->
                    <div class="cg-modal-section">
                        <div class="cg-modal-section-title">
                            <svg viewBox="0 0 24 24">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            Experience
                        </div>

                        <p>{{ selectedCG.experience_years }}+ Years Experience</p>
                    </div>

                </div>
            </div>
        </div>

    </HomeLayout>
</template>

<script lang="js">
import HomeLayout from '../Layouts/HomeLayout.vue';
import Request from "@/utils/apiclient.js";
import statesData from "@/data/states.js";

export default {
    name: "CareGiverLanding",
    components: {
        HomeLayout,
    },
    data() {
        return {
            viewMode: 'grid', // default
            sortBy: 'match',
            caregivers: [],
            isLoading: false,
            states: statesData.states,
            selectedState: '',

            nextPage: true,
            prevPage: true,
            currentPage: 1,
            totalCount: 0,
            limit: 12,

            searchQuery: '',
            filters: {
                state: '',
                type: [],
                experience: [],
                arrangement: [],
                availability: [],
                verified: false
            },

            isModalOpen: false,
            selectedCG: {}
        }
    },
    mounted() {
        document.title = "Browse Care Givers Succour®"
        window.addEventListener('keydown', this.handleEsc);
        this.getCareGiverListings();
    },
    computed: {
        sortedCaregivers() {
            let list = [...this.filteredCaregivers];

            switch (this.sortBy) {
                case 'rating':
                    return list.sort((a, b) => b.rating - a.rating);

                case 'experience':
                    return list.sort((a, b) => b.experience_years - a.experience_years);

                case 'newest':
                    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                default:
                    return list; // match
            }
        },
        activeFilters() {
            let filters = [];

            // VERIFIED
            if (this.filters.verified) {
                filters.push({
                    key: 'verified',
                    value: true,
                    label: '✓ Verified'
                });
            }

            // AVAILABILITY
            this.filters.availability.forEach(val => {
                if (val === 'now') {
                    filters.push({
                        key: 'availability',
                        value: val,
                        label: 'Available now'
                    });
                }
            });

            // TYPE
            this.filters.type.forEach(type => {
                filters.push({
                    key: 'type',
                    value: type,
                    label: this.formatLabel(type)
                });
            });

            // EXPERIENCE
            this.filters.experience.forEach(exp => {
                filters.push({
                    key: 'experience',
                    value: exp,
                    label: this.formatExperience(exp)
                });
            });

            // ARRANGEMENT
            this.filters.arrangement.forEach(arr => {
                filters.push({
                    key: 'arrangement',
                    value: arr,
                    label: this.formatCareType(arr)
                });
            });

            return filters;
        },
        totalPages() {
            return Math.ceil(this.sortedCaregivers.length / this.limit);
        },

        pages() {
            return Array.from(
                { length: this.totalPages },
                (_, i) => i + 1
            );
        },
        paginatedCaregivers() {
            const start = (this.currentPage - 1) * this.limit;
            const end = start + this.limit;
            return this.sortedCaregivers.slice(start, end);
        },
        filteredCaregivers() {
            return this.caregivers.filter((cg) => {
                const q = this.searchQuery.toLowerCase();

                // SEARCH
                const matchesSearch =
                    !q ||
                    cg.name.toLowerCase().includes(q) ||
                    cg.specialities?.some(s =>
                        s.name.toLowerCase().includes(q)
                    );

                // TYPE (from specialties)
                const matchesType =
                    this.filters.type.length === 0 ||
                    this.filters.type.some(type =>
                        cg.specialities.some(s =>
                            s.name.toLowerCase().includes(type)
                        )
                    );

                // EXPERIENCE
                const matchesExp =
                    this.filters.experience.length === 0 ||
                    this.filters.experience.some(exp => {
                        if (exp === '1') return cg.experience_years < 1;
                        if (exp === '1-3') return cg.experience_years >= 1 && cg.experience_years <= 3;
                        if (exp === '3-5') return cg.experience_years >= 3 && cg.experience_years <= 5;
                        if (exp === '5+') return cg.experience_years >= 5;
                    });

                // ARRANGEMENT
                const matchesArrangement =
                    this.filters.arrangement.length === 0 ||
                    this.filters.arrangement.includes(cg.care_type);

                // AVAILABILITY
                const matchesAvailability =
                    this.filters.availability.length === 0 ||
                    (
                        this.filters.availability.includes('now') && cg.available
                    );

                const matchesVerified =
                    this.filters.verified ? cg.verified === true : true;

                return (
                    matchesSearch &&
                    matchesType &&
                    matchesExp &&
                    matchesArrangement &&
                    matchesAvailability &&
                    matchesVerified
                );
            });
        }
    },
    watch: {
        filteredCaregivers() {
            this.currentPage = 1;
        }
    },
    methods: {
        setView(mode) {
            this.viewMode = mode;
        },
        toggleType(type) {
            const i = this.filters.type.indexOf(type);
            i > -1
                ? this.filters.type.splice(i, 1)
                : this.filters.type.push(type);
            this.currentPage = 1;
        },

        toggleArrangement(val) {
            const i = this.filters.arrangement.indexOf(val);
            i > -1
                ? this.filters.arrangement.splice(i, 1)
                : this.filters.arrangement.push(val);
            this.currentPage = 1;
        },

        toggleAvailability(val) {
            const i = this.filters.availability.indexOf(val);

            i > -1
                ? this.filters.availability.splice(i, 1)
                : this.filters.availability.push(val);

            this.currentPage = 1;
        },

        toggleExperience(exp) {
            const i = this.filters.experience.indexOf(exp);
            i > -1
                ? this.filters.experience.splice(i, 1)
                : this.filters.experience.push(exp);
            this.currentPage = 1;
        },
        toggleVerified() {
            this.filters.verified = !this.filters.verified;
            this.currentPage = 1;
        },
        applyFilters() {
            this.currentPage = 1; // reset page
        },
        clearFilters() {
            this.filters = {
                state: '',
                type: [],
                experience: [],
                arrangement: [],
                availability: [], // ✅ keep consistent
                verified: false
            };

            this.searchQuery = '';
            this.selectedState = '';
            this.currentPage = 1;
        },
        async getCareGiverListings() {
            this.isLoading = true;
            this.caregivers = [];

            let url = "/new-caregivers-list/";

            const fetchPage = (url) => {
                return new Promise((resolve) => {
                    const request = new Request(url);

                    request.get((data, error) => {
                        if (error) {
                            console.error("API error:", error);
                            resolve(null);
                        } else {
                            resolve(data);
                        }
                    });
                });
            };

            while (url) {
                const res = await fetchPage(url);

                // console.log("API response:", res);

                if (!res) break;

                // ✅ FIXED HERE
                const list = res.results || [];

                this.caregivers.push(...list);

                url = res.next
                    ? res.next.replace("https://api.succour.care/v1", "")
                    : null;
            }

            // console.log("FINAL caregivers:", this.caregivers);

            this.isLoading = false;
        },
        next() {

            if (this.currentPage < this.totalPages) {
                this.currentPage++;
            } else {
                this.nextPage = !this.nextPage;

            }
        },

        prev() {

            if (this.currentPage > 1) {
                this.currentPage--;
            } else {
                this.prevPage = !this.prevPage;
            }
        },

        goToPage(page) {
            this.currentPage = page;
        },

        requestCG(name) {
            const phone = "2348090804000"; // 🔁 Replace with your WhatsApp number (Nigeria format)
            const msg = encodeURIComponent(`Hi, I'm interested in ${name} from Succour for care services.`);
            window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
        },

        openCGModal(cg) {
            this.selectedCG = cg;
            this.isModalOpen = true;
            document.body.style.overflow = 'hidden';
        },

        closeCGModal() {
            this.isModalOpen = false;
            document.body.style.overflow = '';
        },

        formatCareType(type) {
            if (!type) return '';
            return type
                .replace('_', '-')
                .toLowerCase()
                .replace(/^\w/, (c) => c.toUpperCase());
        },

        handleEsc(e) {
            if (e.key === 'Escape') {
                this.closeCGModal();
            }
        },

        removeFilter(filter) {

            if (filter.key === 'verified') {
                this.filters.verified = false;
            }

            if (filter.key === 'availability') {
                this.filters.availability =
                    this.filters.availability.filter(a => a !== filter.value);
            }

            if (filter.key === 'type') {
                this.filters.type = this.filters.type.filter(t => t !== filter.value);
            }

            if (filter.key === 'experience') {
                this.filters.experience = this.filters.experience.filter(e => e !== filter.value);
            }

            if (filter.key === 'arrangement') {
                this.filters.arrangement = this.filters.arrangement.filter(a => a !== filter.value);
            }

            this.currentPage = 1; // reset pagination
        },
        formatLabel(val) {
            return val
                .replace('_', ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
        },

        formatExperience(exp) {
            if (exp === '1') return 'Less than 1 year';
            if (exp === '1-3') return '1 - 3 years';
            if (exp === '3-5') return '3 - 5 years';
            if (exp === '5+') return '5+ years';
            return exp;

        },


    }
}
</script>

<style lang="css">
:root {
    --pink: #F59CAB;
    --pink-light: #FDE8EC;
    --pink-mid: #F8C4CC;
    --green: #17865F;
    --green-dark: #0f5c41;
    --green-light: #E8F5EF;
    --cream: #FDF9F7;
    --orange: #FF9700;
    --text-dark: #1C2B27;
    --text-mid: #4A5E58;
    --text-light: #8A9E98;
    --border: #E8EAE9;
    --white: #FFFFFF;
}

/* PAGE HERO */
.page-hero {
    background: linear-gradient(135deg, var(--green-light) 0%, var(--pink-light) 100%);
    padding: 44px 3% 36px;
    position: relative;
    overflow: hidden;
}

.page-hero::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 280px;
    height: 280px;
    background: rgba(245, 156, 171, .15);
    border-radius: 50%;
}

.page-hero-inner {
    position: relative;
    z-index: 1;
    max-width: 1200px;
    margin: 0 auto;
}

.page-hero-top {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 28px;
}

.page-hero h1 {
    font-family: 'Lora', serif;
    font-size: clamp(1.6rem, 3vw, 2.2rem);
    font-weight: 400;
    color: var(--text-dark);
    line-height: 1.2;
}

.page-hero h1 em {
    font-style: italic;
    color: var(--green);
}

.hero-stats {
    display: flex;
    align-items: center;
    gap: 20px;
}

.hero-stat {
    text-align: center;
}

.hero-stat-num {
    font-family: 'Lora', serif;
    font-size: 1.5rem;
    font-weight: 400;
    color: var(--green);
}

.hero-stat-lbl {
    font-size: .7rem;
    color: var(--text-mid);
    font-weight: 300;
}

.hero-stat-div {
    width: 1px;
    height: 32px;
    background: var(--border);
}

/* SEARCH BAR */
.search-bar {
    background: white;
    border-radius: 14px;
    padding: 6px 6px 6px 18px;
    display: flex;
    align-items: center;
    gap: 10px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, .08);
    max-width: 680px;
}

.search-bar input {
    flex: 1;
    border: none;
    outline: none;
    font-family: 'Open Sans', sans-serif;
    font-size: .88rem;
    color: var(--text-dark);
    background: none;
}

.search-bar input::placeholder {
    color: var(--text-light);
}

.search-bar select {
    border: none;
    outline: none;
    font-family: 'Open Sans', sans-serif;
    font-size: .84rem;
    color: var(--text-mid);
    background: none;
    cursor: pointer;
    padding: 6px 8px;
    border-left: 1px solid var(--border);
}

.btn-search {
    background: var(--green);
    color: white;
    border: none;
    padding: 10px 22px;
    border-radius: 10px;
    font-family: 'Open Sans', sans-serif;
    font-size: .84rem;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: background .2s;
}

.btn-search:hover {
    background: var(--green-dark);
}

/* LAYOUT */
.page-body {
    max-width: 1200px;
    margin: 0 auto;
    padding: 28px 3% 80px;
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 22px;
    align-items: start;
}

/* FILTER SIDEBAR */
.filter-sidebar {
    background: var(--white);
    border-radius: 16px;
    padding: 22px;
    border: 1.5px solid var(--border);
    position: sticky;
    top: 82px;
}

.filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}

.filter-header h3 {
    font-size: .8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .08em;
    color: var(--text-dark);
}

.filter-clear {
    font-size: .74rem;
    color: var(--pink);
    cursor: pointer;
    font-weight: 600;
    background: none;
    border: none;
    font-family: 'Open Sans', sans-serif;
}

.filter-clear:hover {
    text-decoration: underline;
}

.filter-section {
    margin-bottom: 22px;
    padding-bottom: 22px;
    border-bottom: 1px solid var(--border);
}

.filter-section:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
}

.filter-label {
    font-size: .72rem;
    font-weight: 700;
    color: var(--text-dark);
    letter-spacing: .04em;
    text-transform: uppercase;
    margin-bottom: 10px;
}

.filter-options {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.filter-option {
    display: flex;
    align-items: center;
    gap: 9px;
    cursor: pointer;
    user-select: none;
}

.filter-option input {
    width: 16px;
    height: 16px;
    accent-color: var(--green);
    cursor: pointer;
}

.filter-option label {
    font-size: .82rem;
    color: var(--text-mid);
    cursor: pointer;
    transition: color .2s;
}

.filter-option:hover label {
    color: var(--green);
}

.filter-option.checked label {
    color: var(--text-dark);
    font-weight: 600;
}

.filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.f-chip {
    padding: 6px 12px;
    border-radius: 100px;
    border: 1.5px solid var(--border);
    font-size: .76rem;
    font-weight: 600;
    color: var(--text-mid);
    cursor: pointer;
    transition: all .2s;
    user-select: none;
}

.f-chip:hover {
    border-color: var(--green);
    color: var(--green);
}

.f-chip.active {
    background: var(--green);
    border-color: var(--green);
    color: white;
}

.filter-range {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.filter-range input[type="range"] {
    width: 100%;
    accent-color: var(--green);
}

.range-labels {
    display: flex;
    justify-content: space-between;
    font-size: .7rem;
    color: var(--text-light);
}

.btn-apply-filters {
    width: 100%;
    padding: 11px;
    background: var(--green);
    color: white;
    border: none;
    border-radius: 10px;
    font-family: 'Open Sans', sans-serif;
    font-size: .84rem;
    font-weight: 700;
    cursor: pointer;
    transition: background .2s;
    margin-top: 6px;
}

.btn-apply-filters:hover {
    background: var(--green-dark);
}

/* RESULTS HEADER */
.results-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}

.results-count {
    font-size: .88rem;
    color: var(--text-mid);
}

.results-count strong {
    color: var(--text-dark);
}

.sort-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.sort-label {
    font-size: .78rem;
    color: var(--text-light);
}

.sort-select {
    padding: 8px 32px 8px 12px;
    border: 1.5px solid var(--border);
    border-radius: 9px;
    font-family: 'Open Sans', sans-serif;
    font-size: .82rem;
    color: var(--text-dark);
    background: white;
    cursor: pointer;
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' viewBox='0 0 10 7'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%238A9E98' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
}

.view-toggle {
    display: flex;
    gap: 4px;
}

.view-btn {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1.5px solid var(--border);
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all .2s;
}

.view-btn.active,
.view-btn:hover {
    border-color: var(--green);
    background: var(--green-light);
}

.view-btn svg {
    width: 15px;
    height: 15px;
    stroke: var(--text-mid);
}

.view-btn.active svg {
    stroke: var(--green);
}

/* ACTIVE FILTERS BAR */
.active-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.af-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    background: var(--green-light);
    border: 1px solid #C0DED2;
    border-radius: 100px;
    font-size: .74rem;
    color: var(--green);
    font-weight: 600;
}

.af-tag button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--green);
    font-size: .8rem;
    line-height: 1;
    padding: 0;
    margin-left: 2px;
}

/* CG GRID */
.cg-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 18px;
}

.cg-grid.list-view {
    grid-template-columns: 1fr;
}

.cg-card {
    background: var(--cream);
    border-radius: 20px;
    overflow: hidden;
    transition: transform .25s, box-shadow .25s;
    cursor: pointer;
}

.cg-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, .1);
}

.cg-photo {
    height: 220px;
    position: relative;
    overflow: hidden;
    background: var(--pink-light);
}

.cg-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.cg-verified {
    position: absolute;
    top: 12px;
    right: 12px;
    background: var(--green);
    color: white;
    font-size: .68rem;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 100px;
    letter-spacing: .04em;
}

.cg-info {
    padding: 16px;
}

.cg-name {
    font-weight: 700;
    font-size: .95rem;
    color: var(--text-dark);
    margin-bottom: 2px;
}

.cg-role {
    font-size: .78rem;
    color: var(--text-mid);
    margin-bottom: 6px;
    font-weight: 300;
}

.cg-avail {
    font-size: .75rem;
    color: var(--green);
    display: flex;
    align-items: center;
    gap: 5px;
}

.avail-dot {
    width: 6px;
    height: 6px;
    background: var(--green);
    border-radius: 50%;
}

.cg-btn-row {
    display: flex;
    gap: 8px;
    margin-top: 14px;
}

.cg-btn-request,
.cg-btn-details {
    flex: 1;
    padding: 10px 8px;
    font-family: 'Open Sans', sans-serif;
    font-size: .78rem;
    font-weight: 700;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    text-align: center;
    text-decoration: none;
    display: inline-block;
    transition: opacity .2s, transform .15s;
}

.cg-btn-request {
    background: var(--green);
    color: white;
}

.cg-btn-details {
    background: var(--pink);
    color: white;
}

.cg-btn-request:hover,
.cg-btn-details:hover {
    opacity: .88;
    transform: translateY(-1px);
}

/* LIST VIEW CARD */
.cg-grid.list-view .cg-card {
    display: grid;
    grid-template-columns: 180px 1fr;
}

.cg-grid.list-view .cg-photo {
    height: 100%;
    min-height: 160px;
    border-radius: 0;
}

.cg-grid.list-view .cg-info {
    padding: 20px;
}

.cg-grid.list-view .cg-btn-row {
    max-width: 280px;
}

/* EMPTY STATE */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    grid-column: 1/-1;
}

.empty-state-icon {
    font-size: 3rem;
    margin-bottom: 16px;
}

.empty-state h3 {
    font-family: 'Lora', serif;
    font-size: 1.3rem;
    font-weight: 400;
    color: var(--text-dark);
    margin-bottom: 8px;
}

.empty-state p {
    font-size: .88rem;
    color: var(--text-light);
    font-weight: 300;
}

/* PAGINATION */
.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 40px;
}

.page-btn {
    width: 38px;
    height: 38px;
    border-radius: 9px;
    border: 1.5px solid var(--border);
    background: white;
    font-family: 'Open Sans', sans-serif;
    font-size: .84rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all .2s;
    color: var(--text-mid);
}

.page-btn:hover {
    border-color: var(--green);
    color: var(--green);
}

.page-btn.active {
    background: var(--green);
    border-color: var(--green);
    color: white;
    font-weight: 700;
}

.page-btn.nav {
    width: auto;
    padding: 0 14px;
    gap: 5px;
    font-size: .8rem;
}

/* WA BUBBLE */
#wa-bbl {
    position: fixed;
    bottom: 22px;
    right: 22px;
    z-index: 200;
}

.wa-btn {
    width: 48px;
    height: 48px;
    background: #25D366;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(37, 211, 102, .4);
    animation: waPls 2.5s ease-in-out infinite;
    text-decoration: none;
}

.wa-btn svg {
    width: 22px;
    height: 22px;
    fill: white;
}

.skeleton-box {
    background: #eee;
    height: 180px;
    border-radius: 12px;
}

.skeleton-line {
    height: 12px;
    background: #eee;
    margin: 8px 0;
    border-radius: 6px;
}

.skeleton-line.short {
    width: 60%;
}

@keyframes waPls {

    0%,
    100% {
        box-shadow: 0 4px 16px rgba(37, 211, 102, .4);
    }

    50% {
        box-shadow: 0 4px 26px rgba(37, 211, 102, .65), 0 0 0 6px rgba(37, 211, 102, .1);
    }
}

/* FOOTER */
footer {
    background: var(--text-dark);
    padding: 56px 6% 28px;
}

.footer-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 48px;
    margin-bottom: 48px;
}

.footer-logo-text {
    font-size: 1.2rem;
    font-weight: 700;
    color: white;
    font-family: 'Open Sans', sans-serif;
}

.footer-logo-text sup {
    font-size: .46rem;
    color: rgba(255, 255, 255, .3);
}

.footer-tagline {
    font-size: .8rem;
    color: rgba(255, 255, 255, .4);
    line-height: 1.7;
    margin: 12px 0 20px;
    font-weight: 300;
    max-width: 220px;
}

.footer-socials {
    display: flex;
    gap: 8px;
}

.social-btn {
    width: 34px;
    height: 34px;
    background: rgba(255, 255, 255, .07);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: background .2s;
}

.social-btn:hover {
    background: var(--pink);
}

.social-btn svg {
    width: 15px;
    height: 15px;
    fill: rgba(255, 255, 255, .5);
}

.social-btn:hover svg {
    fill: white;
}

.footer-col h4 {
    font-size: .7rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, .3);
    margin-bottom: 16px;
}

.footer-col ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.footer-col a {
    font-size: .82rem;
    color: rgba(255, 255, 255, .5);
    text-decoration: none;
    font-weight: 300;
    transition: color .2s;
}

.footer-col a:hover {
    color: var(--pink);
}

.footer-bottom {
    border-top: 1px solid rgba(255, 255, 255, .07);
    padding-top: 20px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.footer-bottom p {
    font-size: .74rem;
    color: rgba(255, 255, 255, .22);
    font-weight: 300;
}

/* RESPONSIVE */
/* ── Responsive ── */
@media(max-width:1280px) {
    .cg-grid {
        grid-template-columns: repeat(4, 1fr);
    }

    .page-body {
        grid-template-columns: 200px 1fr;
    }
}

@media(max-width:1024px) {
    .cg-grid {
        grid-template-columns: repeat(3, 1fr);
    }

    .page-body {
        grid-template-columns: 190px 1fr;
        padding: 24px 2% 60px;
    }

    .footer-grid {
        grid-template-columns: 1fr 1fr;
    }
}

@media(max-width:768px) {
    .nav-links {
        display: none;
    }

    .nav-ctas .btn-ghost:first-child {
        display: none;
    }

    .ribbon {
        padding: 8px 14px;
        font-size: .76rem;
    }

    .page-body {
        grid-template-columns: 1fr;
    }

    .filter-sidebar {
        position: static;
        display: none;
    }

    .filter-sidebar.mobile-open {
        display: block;
    }

    .page-hero {
        padding: 36px 5% 32px;
    }

    .page-hero h1 {
        font-size: 1.5rem;
    }

    .search-bar select {
        display: none;
    }

    .cg-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 14px;
    }

    .cg-photo {
        height: 180px;
    }

    .cg-btn-request,
    .cg-btn-details {
        font-size: .72rem;
        padding: 9px 6px;
    }

    .footer-grid {
        grid-template-columns: 1fr;
        gap: 24px;
    }

    .results-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
}

@media(max-width:480px) {
    .cg-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }

    .cg-photo {
        height: 150px;
    }

    .cg-name {
        font-size: .84rem;
    }

    .cg-role {
        font-size: .72rem;
    }

    .cg-avail {
        font-size: .68rem;
    }

    .cg-btn-request,
    .cg-btn-details {
        font-size: .68rem;
        padding: 8px 4px;
        border-radius: 8px;
    }

    .page-hero {
        padding: 28px 5%;
    }

    .hero-stats {
        display: none;
    }

    .footer-grid {
        grid-template-columns: 1fr;
    }
}

/* ── CARER MODAL ── */
.cg-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .45);
    z-index: 3000;
    display: none;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.cg-modal-overlay.open {
    display: flex;
}

.cg-modal {
    background: white;
    border-radius: 20px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, .18);
    overflow: hidden;
    animation: modalIn .25s ease;
}

@keyframes modalIn {
    from {
        opacity: 0;
        transform: translateY(16px)
    }

    to {
        opacity: 1;
        transform: translateY(0)
    }
}

.cg-modal-header {
    padding: 22px 22px 16px;
    display: flex;
    align-items: center;
    gap: 14px;
    position: relative;
    border-bottom: 1px solid rgba(0, 0, 0, .07);
}

.cg-modal-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Open Sans', sans-serif;
    font-size: 1.1rem;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
}

.cg-modal-name {
    font-family: 'Open Sans', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    color: #1C2B27;
    display: flex;
    align-items: center;
    gap: 6px;
}

.cg-modal-name span {
    width: 18px;
    height: 18px;
    background: #17865F;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: .6rem;
    color: white;
}

.cg-modal-role {
    font-size: .78rem;
    color: #4A5E58;
    font-weight: 300;
    margin-top: 2px;
}

.cg-modal-close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1.5px solid rgba(0, 0, 0, .12);
    background: white;
    cursor: pointer;
    font-size: .85rem;
    color: #4A5E58;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .2s;
}

.cg-modal-close:hover {
    background: #FDE8EC;
}

.cg-modal-body {
    padding: 18px 22px 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.cg-modal-wa {
    display: block;
    background: #F59CAB;
    color: white;
    text-align: center;
    padding: 11px;
    border-radius: 100px;
    font-family: 'Open Sans', sans-serif;
    font-size: .85rem;
    font-weight: 700;
    text-decoration: none;
    margin-bottom: 4px;
    transition: background .2s;
}

.cg-modal-wa:hover {
    background: #e8849a;
}

.cg-modal-section {
    background: #F3F6F5;
    border-radius: 12px;
    padding: 14px 16px;
}

.cg-modal-section-title {
    font-size: .72rem;
    font-weight: 700;
    color: #1C2B27;
    text-transform: uppercase;
    letter-spacing: .07em;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 7px;
}

.cg-modal-section-title svg {
    width: 14px;
    height: 14px;
    stroke: #17865F;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
}

.cg-modal-section ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.cg-modal-section ul li {
    font-size: .82rem;
    color: #4A5E58;
    font-weight: 300;
}

.cg-modal-section ul li::before {
    content: '• ';
    color: #17865F;
}

.cg-modal-section p {
    font-size: .82rem;
    color: #4A5E58;
    font-weight: 300;
}

@media(max-width:480px) {
    .cg-modal {
        border-radius: 16px 16px 0 0;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        max-width: 100%;
    }
}
</style>